// Location: Corporate-dashboard/Container/components/dashboard/DegreeAnalysis.js

import React, { useState, useRef, useEffect } from 'react';
import { Card, Row, Col, Typography, Spin } from 'antd';
import { getElementAtEvent } from 'react-chartjs-2';
import { useSelector, useDispatch } from 'react-redux';
import { getDegreeChartData } from '../../../actions';
import jsPDF from "jspdf";
import { generateInternshipRolesPDF } from '../utils/generateInternshipRolesPDF';

// Import sub-components
import BarChartDisplay from './BarChartDisplay';
// Make sure this is uncommented if you want to donut chart
import GenderDonutChart from './GenderDonutChart';

const { Title } = Typography;

// Data specific to this section - now using API data

const DegreeAnalysis = ({ selectedFilter, isExporting = false, degreeDataRef, setChartRangeRef, setIndividualExportLoading, setExportMessage }) => {
  const dispatch = useDispatch();
  const degreeChartData = useSelector(state => state?.corporateDashboardPage2?.degreeChartData);
  const degreeChartLoading = useSelector(state => state?.corporateDashboardPage2?.degreeChartLoading);
  const corporateData = useSelector((state) => state?.auth?.corporateData);
  const [selectedDegree, setSelectedDegree] = useState('');
  const [startIndex, setStartIndex] = useState(0);
  const [chartRange, setChartRange] = useState(null);
  const barChartRef = useRef();
  const containerRef = useRef();

  // Function to get dynamic label based on selected filter
  const getDynamicLabel = (key, selectedFilter) => {
    if (key === "internshipRoles") {
        const filterName = selectedFilter === "JOBS" ? "Jobs" : 
                          selectedFilter === "INTERNSHIP" ? "Internship" : 
                          selectedFilter === "INTERNSHIP_PPO" ? "Internship + PPO" :
                          selectedFilter === "APPRENTICESHIP" ? "Apprenticeship" : 
                          selectedFilter === "PROJECTS" ? "Projects" : "Roles";
        return `No. of ${filterName} by Degree`;
    }
    return null;
  }

  // Export functionality
  const handleExport = async () => {
    try {
      setIndividualExportLoading(true);
      setExportMessage('Exporting Degree Roles...');
      const pdf = new jsPDF("l", "mm", "a4"); // Use landscape orientation

      await generateInternshipRolesPDF({
        pdf,
        corporateData,
        selectedFilter,
        exportRefs: {
          internshipRoles: containerRef,
          internshipRolesData: degreeDataRef,
          setChartRange: setChartRangeRef
        },
        getDynamicLabel
      });

      pdf.save("Degree_Roles.pdf");
    } finally {
      setIndividualExportLoading(false);
    }
  };

  // Expose setChartRange to parent for PDF export control
  useEffect(() => {
    if (setChartRangeRef) {
      setChartRangeRef.current = setChartRange;
    }
  }, [setChartRangeRef]);

  // Reset selected degree when filter changes to clear gender chart selection
  useEffect(() => {
    setSelectedDegree('');
  }, [selectedFilter]);

  // Use API data
  const allDetailsData = degreeChartData?.degreeBarChart || [];

  // Check if there's any data
  const hasData = allDetailsData.some(item => 
    item.degree && item.degree !== "" && (
        (item.floated || 0) > 0 || 
        (item.active || 0) > 0 || 
        (item.closed || 0) > 0
    )
  );

  // Expose degree data to parent via ref for PDF export
  useEffect(() => {
    if (degreeDataRef) {
      degreeDataRef.current = allDetailsData;
    }
  }, [allDetailsData, degreeDataRef]);

  // Extract gender data from API response
  const genderAnalysisData = {};
  const genderSelectedData = {};
  
  allDetailsData.forEach(item => {
    if (item.degreeGenderAnalysis) {
      const { men, women, others = 0 } = item.degreeGenderAnalysis;
      genderAnalysisData[item.degree] = { men, women };
      genderSelectedData[item.degree] = { men: [men], women: [women] };
    }
  });

  // Extract labels from data
  const labels = allDetailsData.map(item => item.degree);
  const itemsPerPage = 6;
  // Use chartRange as reliable export signal (survives async re-renders)
  const isInExportMode = isExporting || chartRange !== null;
  const effectiveItemsPerPage = chartRange ? chartRange.count : (isInExportMode ? allDetailsData.length : itemsPerPage);
  const effectiveStartIndex = chartRange ? chartRange.start : (isInExportMode ? 0 : startIndex);
  const visibleLabels = labels.slice(effectiveStartIndex, effectiveStartIndex + effectiveItemsPerPage);
  
  // Extract data for chart from structured format
  const allData = {
    floated: allDetailsData.map(item => item.floated),
    active: allDetailsData.map(item => item.active),
    closed: allDetailsData.map(item => item.closed),
  };
  
  const barData = {
    labels: visibleLabels,
    datasets: [
      { label: 'Floated', data: allData.floated.slice(effectiveStartIndex, effectiveStartIndex + effectiveItemsPerPage), backgroundColor: '#001F54', barPercentage: 0.6, categoryPercentage: 0.7 },
      { label: 'Active', data: allData.active.slice(effectiveStartIndex, effectiveStartIndex + effectiveItemsPerPage), backgroundColor: '#ADE8F4', barPercentage: 0.6, categoryPercentage: 0.7 },
      { label: 'Closed', data: allData.closed.slice(effectiveStartIndex, effectiveStartIndex + effectiveItemsPerPage), backgroundColor: '#D9D9D9', barPercentage: 0.6, categoryPercentage: 0.7 },
    ],
  };

  // Calculate dynamic y-axis maximum
  const allValues = [...allData.floated, ...allData.active, ...allData.closed];
  const maxValue = Math.max(...allValues);
  const yAxisMax = Math.ceil((maxValue * 1.2) / 5) * 5; // Add 20% padding and round to nearest 5

  const barOptions = {
    responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } },
    scales: { 
      x: { 
        grid: { display: false }, 
        ticks: { 
          color: (context) => {
            const label = context.label;
            return selectedDegree === label ? '#1890ff' : '#595959';
          }, 
          font: { weight: 'bold', size: 11 } 
        }, 
        title: { display: false } // Hide x-axis title 
      }, 
      y: { beginAtZero: true, max: yAxisMax, grid: { color: '#f0f0f0' }, ticks: { color: '#8c8c8c', stepSize: Math.ceil(yAxisMax / 8) }, title: { display: true, text: 'No. of roles', font: { size: 14, weight: 'bold' }, padding: { bottom: 10 }, color: '#262626' } }, 
    },
    barThickness: 10,
    onClick: (event) => { if (!barChartRef.current) return; const elements = getElementAtEvent(barChartRef.current, event); if (elements.length > 0) { const actualIndex = startIndex + elements[0].index; setSelectedDegree(labels[actualIndex]); } }
  };

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectedDegree('');
  };

  // Navigation functions
  const handlePrevious = () => {
    setStartIndex(Math.max(0, startIndex - 1));
  };

  const handleNext = () => {
    const maxStart = Math.max(0, labels.length - itemsPerPage);
    setStartIndex(Math.min(maxStart, startIndex + 1));
  };

  // Corrected colors to match image
  const legendItems = [
    { label: 'Floated', color: '#001F54' }, 
    { label: 'Active', color: '#ADE8F4' }, 
    { label: 'Closed', color: '#D9D9D9' }
  ]; // Always show legend for now

  return (
    // This Card contains both charts
    <Card className="cd-card" bordered={true} style={{ width: '100%', height: '100%' }} bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .full-height-spin-wrapper {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .full-height-spin-wrapper .ant-spin-blur {
          opacity: 0.5;
          user-select: none;
        }
        .full-height-spin-wrapper .ant-spin-container {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
      `}</style>
      <Spin spinning={degreeChartLoading} wrapperClassName="full-height-spin-wrapper">
        <Row gutter={[24, 24]} style={{ flexGrow: 1, minHeight: 0 }}>
          
          {/* Column 1: Bar Chart */}
          <Col xs={24} lg={isInExportMode ? 24 : 12}>
            {/* Title moved inside BarChartDisplay for better encapsulation */}
            <BarChartDisplay
               barChartRef={barChartRef}
               containerRef={containerRef}
               barOptions={barOptions}
               barData={barData}
               legendItems={legendItems}
               selectedDegree={selectedDegree}
               allDetailsData={allDetailsData}
               selectedFilter={selectedFilter}
               startIndex={startIndex}
               totalItems={labels.length}
               itemsPerPage={itemsPerPage}
               onPrevious={handlePrevious}
               onNext={handleNext}
               isExporting={isInExportMode}
               onClearSelection={handleClearSelection}
               onBarClick={(event, elements) => {
                 if (elements && elements.length > 0) {
                   const actualIndex = startIndex + elements[0].index;
                   setSelectedDegree(labels[actualIndex]);
                 }
               }}
            />
          </Col>

          {/* Column 2: Gender Donut Chart */}
          {!isInExportMode && (
          <Col xs={14} lg={12}>
            <GenderDonutChart
               degree={selectedDegree}
               genderAnalysisData={genderAnalysisData}
               genderSelectedData={genderSelectedData}
               selectedFilter={selectedFilter}
               degreeDataRef={degreeDataRef}
               setChartRangeRef={setChartRangeRef}
               onExport={handleExport}
               hasData={hasData}
            />
          </Col>
          )}
        </Row>
      </Spin>
    </Card>
  );
};

export default DegreeAnalysis;